import type { DesignItem } from '@core/types';
import { CSS_NAMED_COLORS } from './svgColorNames';

export type SvgColorMap = Record<string, string>;

const SVG_COLOR_LIMIT = 12;
const sourceCache = new Map<string, Promise<string>>();

/**
 * Small LRU for parse-heavy results. Re-selecting a layer, loading a saved design or dragging a
 * color picker back and forth would otherwise re-parse and re-serialize the same SVG each time.
 */
const MEMO_LIMIT = 64;
const recolorMemo = new Map<string, string>();
const paletteMemo = new Map<string, string[]>();

function memo<T>(cache: Map<string, T>, key: string, compute: () => T): T {
  const hit = cache.get(key);
  if (hit !== undefined) {
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }
  const value = compute();
  cache.set(key, value);
  if (cache.size > MEMO_LIMIT) cache.delete(cache.keys().next().value as string);
  return value;
}

/** FNV-1a over the markup plus its length: a cheap key that avoids holding big strings twice. */
function sourceKey(source: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < source.length; i++) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${source.length}:${(hash >>> 0).toString(36)}`;
}

function svgDataUrl(markup: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
}

/** Fetch sanitized SVG source once per stable server URL. Failed requests remain retryable. */
export function fetchSvgSource(url: string): Promise<string> {
  const sourceUrl = String(url ?? '').trim();
  if (!sourceUrl) return Promise.reject(new Error('Missing SVG source URL'));

  const cached = sourceCache.get(sourceUrl);
  if (cached) return cached;

  const pending = fetch(sourceUrl, { credentials: 'same-origin' })
    .then(async (response) => {
      if (!response.ok) throw new Error(`SVG request failed (${response.status})`);
      return response.text();
    })
    .catch((error) => {
      sourceCache.delete(sourceUrl);
      throw error;
    });
  sourceCache.set(sourceUrl, pending);
  return pending;
}

/** Normalize editable #rgb/#rrggbb values to lowercase #rrggbb. */
export function normalizeSvgHex(value: string): string | null {
  const color = String(value ?? '').trim();
  const short = /^#([0-9a-f]{3})$/i.exec(color);
  if (short) {
    const [r, g, b] = short[1]!.split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  const full = /^#([0-9a-f]{6})$/i.exec(color);
  return full ? `#${full[1]!.toLowerCase()}` : null;
}

function parseRgbChannel(raw: string): number | null {
  const percent = /^([+-]?\d*\.?\d+)%$/.exec(raw);
  const numeric = percent ? (Number(percent[1]) / 100) * 255 : /^[+-]?\d*\.?\d+$/.test(raw) ? Number(raw) : NaN;
  if (!Number.isFinite(numeric)) return null;
  return Math.min(255, Math.max(0, Math.round(numeric)));
}

/**
 * Normalize any editable paint (#hex, CSS named color, rgb()/rgba()) to
 * lowercase #rrggbb. Non-editable paints (none, currentColor, url(...),
 * semi-transparent rgba — rewriting it to hex would drop opacity) return null.
 */
export function normalizeSvgColor(value: string): string | null {
  const color = String(value ?? '').trim();
  if (!color) return null;
  const hex = normalizeSvgHex(color);
  if (hex) return hex;
  const named = CSS_NAMED_COLORS[color.toLowerCase()];
  if (named) return named;
  const rgb = /^rgba?\(([^)]*)\)$/i.exec(color);
  if (!rgb) return null;
  const parts = rgb[1]!.split(/[\s,/]+/).filter(Boolean);
  if (parts.length < 3 || parts.length > 4) return null;
  if (parts.length === 4) {
    const alphaRaw = parts[3]!;
    const alpha = alphaRaw.endsWith('%') ? Number(alphaRaw.slice(0, -1)) / 100 : Number(alphaRaw);
    if (!Number.isFinite(alpha) || alpha < 1) return null;
  }
  const channels = parts.slice(0, 3).map(parseRgbChannel);
  if (channels.some((channel) => channel === null)) return null;
  return `#${channels.map((channel) => channel!.toString(16).padStart(2, '0')).join('')}`;
}

function parseSvg(source: string): XMLDocument {
  const documentNode = new DOMParser().parseFromString(source, 'image/svg+xml');
  const root = documentNode.documentElement;
  if (
    !root
    || root.localName.toLowerCase() !== 'svg'
    || documentNode.getElementsByTagName('parsererror').length > 0
  ) {
    throw new Error('Invalid SVG source');
  }
  return documentNode;
}

function svgElements(documentNode: XMLDocument): Element[] {
  const nodes = documentNode.documentElement.getElementsByTagName('*');
  return [documentNode.documentElement, ...Array.from(nodes)] as Element[];
}

function inlineStyleColors(style: string): string[] {
  const colors: string[] = [];
  const declaration = /(?:^|;)\s*(?:fill|stroke)\s*:\s*([^;]+)/gi;
  let match: RegExpExecArray | null;
  while ((match = declaration.exec(style)) !== null) {
    const normalized = normalizeSvgColor(match[1] ?? '');
    if (normalized) colors.push(normalized);
  }
  return colors;
}

/** Concatenated text of a <style> element (xmldom-safe; no textContent). */
function styleElementText(element: Element): string {
  let text = '';
  for (let node = element.firstChild; node; node = node.nextSibling) {
    if (node.nodeType === 3 || node.nodeType === 4) text += node.nodeValue ?? '';
  }
  return text;
}

function setStyleElementText(element: Element, css: string) {
  while (element.firstChild) element.removeChild(element.firstChild);
  element.appendChild(element.ownerDocument!.createTextNode(css));
}

/**
 * Rewrite each flat `selector { body }` rule body. Comments are stripped;
 * stylesheets containing at-rules are passed through untouched — the upload
 * sanitizer rejects @import, and flat regex parsing would mis-nest their braces.
 */
function forEachStyleRuleBody(css: string, rewrite: (body: string, selector: string) => string): string {
  const stripped = css.replace(/\/\*[\s\S]*?\*\//g, '');
  if (stripped.includes('@')) return css;
  return stripped.replace(
    /([^{}]+)\{([^{}]*)\}/g,
    (_rule, selector: string, body: string) => `${selector}{${rewrite(body, selector)}}`,
  );
}

const PAINTABLE_TAGS = new Set(['path', 'rect', 'circle', 'ellipse', 'polygon', 'polyline', 'text', 'tspan']);
const FILL_DECLARATION = /(?:^|;)\s*fill\s*:/i;

/** Class names given a fill by any <style> rule (simple `.name` selectors only). */
function fillRuleClasses(documentNode: XMLDocument): Set<string> {
  const classes = new Set<string>();
  for (const element of svgElements(documentNode)) {
    if (element.localName.toLowerCase() !== 'style') continue;
    forEachStyleRuleBody(styleElementText(element), (body, selector) => {
      if (FILL_DECLARATION.test(body)) {
        for (const token of selector.split(',')) {
          const named = /^\s*\.([\w-]+)\s*$/.exec(token);
          if (named) classes.add(named[1]!);
        }
      }
      return body;
    });
  }
  return classes;
}

/**
 * True when at least one paintable shape inherits the SVG default black fill —
 * i.e. neither it nor any ancestor specifies fill via attribute, inline style,
 * or a class-based <style> rule.
 */
function hasImplicitBlackFill(documentNode: XMLDocument): boolean {
  const classesWithFill = fillRuleClasses(documentNode);
  const specifiesFill = (element: Element): boolean => {
    if (element.hasAttribute('fill')) return true;
    if (FILL_DECLARATION.test(element.getAttribute('style') ?? '')) return true;
    const classAttr = element.getAttribute('class');
    if (classAttr && classAttr.split(/\s+/).some((name) => classesWithFill.has(name))) return true;
    return false;
  };
  for (const element of svgElements(documentNode)) {
    if (!PAINTABLE_TAGS.has(element.localName.toLowerCase())) continue;
    let specified = false;
    let node: Element | null = element;
    while (node && node.nodeType === 1) {
      if (specifiesFill(node)) {
        specified = true;
        break;
      }
      node = node.parentNode as Element | null;
    }
    if (!specified) return true;
  }
  return false;
}

/** Extract editable colors in document order from attributes, inline styles, and <style> rules. */
export function extractPalette(source: string, limit = SVG_COLOR_LIMIT): string[] {
  return [...memo(paletteMemo, `${sourceKey(source)}|${limit}`, () => computePalette(source, limit))];
}

function computePalette(source: string, limit: number): string[] {
  const documentNode = parseSvg(source);
  const palette: string[] = [];
  const add = (raw: string | null) => {
    const normalized = normalizeSvgColor(raw ?? '');
    if (normalized && !palette.includes(normalized) && palette.length < limit) {
      palette.push(normalized);
    }
  };

  for (const element of svgElements(documentNode)) {
    add(element.getAttribute('fill'));
    add(element.getAttribute('stroke'));
    for (const color of inlineStyleColors(element.getAttribute('style') ?? '')) add(color);
    if (element.localName.toLowerCase() === 'style') {
      forEachStyleRuleBody(styleElementText(element), (body) => {
        for (const color of inlineStyleColors(body)) add(color);
        return body;
      });
    }
    if (palette.length >= limit) break;
  }
  if (palette.length < limit && !palette.includes('#000000') && hasImplicitBlackFill(documentNode)) {
    palette.push('#000000');
  }
  return palette;
}

function normalizedColorMap(colors: SvgColorMap): Map<string, string> {
  const normalized = new Map<string, string>();
  for (const [original, replacement] of Object.entries(colors ?? {})) {
    const from = normalizeSvgColor(original);
    const to = normalizeSvgHex(replacement);
    if (from && to) normalized.set(from, to);
  }
  return normalized;
}

function recolorInlineStyle(style: string, colors: Map<string, string>): string {
  return style.replace(
    /((?:^|;)\s*(?:fill|stroke)\s*:\s*)([^;]+)/gi,
    (declaration, prefix: string, value: string) => {
      const original = normalizeSvgColor(value);
      return original && colors.has(original)
        ? `${prefix}${colors.get(original)}`
        : declaration;
    },
  );
}

function injectViewBoxDimensions(root: Element) {
  const parts = String(root.getAttribute('viewBox') ?? '')
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length < 4 || !(parts[2]! > 0) || !(parts[3]! > 0)) return;
  if (!root.hasAttribute('width')) root.setAttribute('width', String(parts[2]));
  if (!root.hasAttribute('height')) root.setAttribute('height', String(parts[3]));
}

/** Recolor from original source and serialize an export/browser-ready SVG. */
export function applyRecolor(source: string, svgColors: SvgColorMap = {}): string {
  const colorKey = Object.entries(svgColors ?? {}).sort(([a], [b]) => a.localeCompare(b)).map(([a, b]) => `${a}>${b}`).join(',');
  return memo(recolorMemo, `${sourceKey(source)}|${colorKey}`, () => computeRecolor(source, svgColors));
}

function computeRecolor(source: string, svgColors: SvgColorMap): string {
  const documentNode = parseSvg(source);
  const colors = normalizedColorMap(svgColors);

  const implicitBlack = colors.has('#000000') && hasImplicitBlackFill(documentNode);

  for (const element of svgElements(documentNode)) {
    for (const attribute of ['fill', 'stroke']) {
      const original = normalizeSvgColor(element.getAttribute(attribute) ?? '');
      if (original && colors.has(original)) {
        element.setAttribute(attribute, colors.get(original)!);
      }
    }
    const style = element.getAttribute('style');
    if (style) element.setAttribute('style', recolorInlineStyle(style, colors));
    if (element.localName.toLowerCase() === 'style') {
      const css = styleElementText(element);
      const rewritten = forEachStyleRuleBody(css, (body) => recolorInlineStyle(body, colors));
      if (rewritten !== css) setStyleElementText(element, rewritten);
    }
  }

  // Shapes with no fill anywhere in their ancestor chain inherit this root
  // presentation attribute; explicitly filled subtrees are unaffected.
  if (implicitBlack && !documentNode.documentElement.hasAttribute('fill')) {
    documentNode.documentElement.setAttribute('fill', colors.get('#000000')!);
  }

  injectViewBoxDimensions(documentNode.documentElement);
  return new XMLSerializer().serializeToString(documentNode.documentElement);
}

export function resolveSvgSourceUrl(item: DesignItem): string {
  const ex = item as Record<string, unknown>;
  for (const candidate of [item.fileUrl, ex.file, item.src]) {
    const source = String(candidate ?? '').trim();
    if (source && !source.startsWith('blob:')) return source;
  }
  return '';
}

export async function loadSvgItemSource(item: DesignItem): Promise<string> {
  const inlineSource = String((item as Record<string, unknown>).shapeSourceSvg ?? '').trim();
  if (inlineSource.includes('<svg')) return inlineSource;
  return fetchSvgSource(resolveSvgSourceUrl(item));
}

/** Fetch original source, apply persisted colors, and replace runtime markup/blob URL. */
export async function rehydrateSvgItem(item: DesignItem): Promise<string> {
  const sourceUrl = resolveSvgSourceUrl(item);
  const inlineSource = String((item as Record<string, unknown>).shapeSourceSvg ?? '').trim();
  if (!sourceUrl && !inlineSource) throw new Error('Missing SVG source URL');

  const source = await loadSvgItemSource(item);
  const markup = applyRecolor(source, item.svgColors ?? {});
  const previousSrc = String(item.src ?? '');
  const nextSrc = sourceUrl.startsWith('data:image/svg+xml')
    || inlineSource
      ? svgDataUrl(markup)
      : URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }));

  item.svg = markup;
  item.src = nextSrc;
  if (previousSrc.startsWith('blob:') && previousSrc !== nextSrc) {
    URL.revokeObjectURL(previousSrc);
  }
  return markup;
}
